import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import productsData from "../data/productsData";
import Seo from "../components/Seo";
import { PAGES, SITE } from "../seo/meta.mjs";

const productsJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: productsData.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Product",
      name: p.name,
      description: p.description,
      image: SITE.origin + p.image_url,
      brand: { "@type": "Brand", name: "YTEK Controls" },
      manufacturer: { "@id": SITE.origin + "/#organization" },
    },
  })),
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    setProducts(productsData);
  }, []);

  const categories = [
    "all",
    ...new Set(products.map((p) => p?.category).filter(Boolean)),
  ];

  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((p) => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-20">
      <Seo {...PAGES.products} jsonLd={productsJsonLd} />

      {/* HERO */}

      <section className="py-24 bg-slate-950">

        <div className="max-w-7xl mx-auto px-6 md:px-12">

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6 text-center"
          >
            Products & <span className="text-ocean-500">Solutions</span>
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto text-center"
          >
            Comprehensive range of control systems and automation solutions
          </motion.p>

        </div>

      </section>

      {/* FILTER */}

      <section className="py-12 bg-slate-900">

        <div className="max-w-7xl mx-auto px-6 md:px-12">

          <div className="flex flex-wrap gap-3 justify-center">

            {categories.map((category) => (

              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 font-medium uppercase tracking-widest text-xs transition-all ${
                  selectedCategory === category
                    ? "bg-ocean-500 text-white"
                    : "border border-white/20 text-slate-400 hover:border-white hover:text-white"
                }`}
              >
                {category.replace("_", " ")}

              </button>

            ))}

          </div>

        </div>

      </section>

      {/* PRODUCTS */}

      <section className="py-12 pb-24 bg-slate-950">

        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {filteredProducts.length === 0 ? (

            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">
                No products available.
              </p>
            </div>

          ) : (

            <div className="space-y-16">

              {filteredProducts.map((product) => (

                <div
                  key={product.product_id}
                  className="bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shadow-md rounded-lg overflow-hidden group"
                >

                  <div className="grid lg:grid-cols-2">

                    {/* IMAGE COLUMN */}

                    <div className="relative bg-slate-100">

                      <img
                        src={product.image_url}
                        alt={product.name}
                        className={`w-full h-full transition-transform duration-500 group-hover:scale-105
                        ${
                          product.product_id === "gov"
                            ? "object-contain p-8"
                            : "object-cover"
                        }`}
                      />

                    </div>

                    {/* CONTENT COLUMN */}

                    <div className="p-10 lg:p-12">

                      <div className="mb-4">

                        <span className="px-3 py-1 bg-ocean-500/20 border border-ocean-500/30 text-xs text-ocean-400 uppercase tracking-wider">
                          {product.category.replace("_", " ")}
                        </span>

                      </div>

                      <h3 className="font-heading text-3xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
                        {product.name}
                      </h3>

                      <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                        {product.description}
                      </p>

                      {/* FEATURES */}

                      {product.features && (

                        <div className="mb-6">

                          <h4 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100 mb-3">
                            Features
                          </h4>

                          <ul className="space-y-2">

                            {product.features.map((feature, idx) => (

                              <li
                                key={idx}
                                className="flex items-start gap-2 text-slate-600 dark:text-slate-400"
                              >

                                <CheckCircle2
                                  size={16}
                                  className="mt-1 text-ocean-500"
                                />

                                {feature}

                              </li>

                            ))}

                          </ul>

                        </div>

                      )}

                      {/* APPLICATIONS */}

                      {product.applications && (

                        <div className="mb-6">

                          <h4 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100 mb-3">
                            Applications
                          </h4>

                          <div className="flex flex-wrap gap-2">

                            {product.applications.map((app, idx) => (

                              <span
                                key={idx}
                                className="px-3 py-1 bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-300"
                              >
                                {app}
                              </span>

                            ))}

                          </div>

                        </div>

                      )}

                      {/* TECHNICAL */}

                      {product.technical_specs && (

                        <div className="bg-slate-200 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 p-4">

                          <h4 className="font-mono text-sm text-ocean-400 mb-2 uppercase tracking-wider">
                            Technical Specifications
                          </h4>

                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {product.technical_specs}
                          </p>

                        </div>

                      )}

                    </div>

                  </div>

                </div>

              ))}

            </div>

          )}

        </div>

      </section>

    </div>
  );
};

export default Products;